import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { PackagePlus, Plus, Loader2, Search, MoreHorizontal, Trash2, Lock, Truck, CheckCircle2, X as XIcon, Trash, ScanLine, Pencil, AlertTriangle, Download, ScanBarcode } from "lucide-react";
import { formatDate } from "../lib/format";
import { subscribe } from "../lib/sync";
import DeleteInventoryItemDialog from "../components/DeleteInventoryItemDialog";
import NewExpectedDeliveryDialog from "../components/NewExpectedDeliveryDialog";
import ScanInventoryDialog from "../components/ScanInventoryDialog";
import BarcodeScannerDialog from "../components/BarcodeScannerDialog";
import ItemCombobox from "../components/ItemCombobox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PrintButton, PrintHeader } from "../components/Print";

function NewItemDialog({ open, onOpenChange, onSuccess, defaultCategory = "office", vendors = [] }) {
  const [form, setForm] = useState({ sku: "", name: "", unit: "each", quantity: 0, category: defaultCategory, reorder_point: "", vendor_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ sku: "", name: "", unit: "each", quantity: 0, category: defaultCategory, reorder_point: defaultCategory === "janitorial" ? "5" : "", vendor_id: "" });
  }, [open, defaultCategory]);

  const janitorial = form.category === "janitorial";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sku.trim() || !form.name.trim()) {
      return toast.error("SKU and Name are required");
    }
    if (janitorial && (form.reorder_point === "" || Number(form.reorder_point) < 0)) {
      return toast.error("Janitorial items need a reorder point (0 or higher)");
    }
    setBusy(true);
    try {
      const payload = {
        sku: form.sku.trim(),
        name: form.name.trim(),
        unit: form.unit.trim() || "each",
        quantity: Number(form.quantity) || 0,
        category: form.category || "office",
      };
      if (form.reorder_point !== "") payload.reorder_point = Number(form.reorder_point);
      if (form.vendor_id) payload.vendor_id = form.vendor_id;
      await api.post("/inventory/items", payload);
      toast.success(`Added "${form.name}" to ${janitorial ? "Janitorial" : "Office"} inventory`);
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add item");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="new-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Add New Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">SKU</Label>
              <Input
                required
                value={form.sku}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                data-testid="new-item-sku-input"
                className="rounded-sm mt-1.5 font-mono"
                placeholder="HW-1001"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm((f) => ({ ...f, unit: v }))}>
                <SelectTrigger data-testid="new-item-unit-select" className="rounded-sm mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">each</SelectItem>
                  <SelectItem value="box">box</SelectItem>
                  <SelectItem value="roll">roll</SelectItem>
                  <SelectItem value="pallet">pallet</SelectItem>
                  <SelectItem value="ft">ft</SelectItem>
                  <SelectItem value="lb">lb</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="new-item-category-select" className="rounded-sm mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="office">Office Furniture & Supply</SelectItem>
                <SelectItem value="janitorial">Janitorial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Item Name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="new-item-name-input"
              className="rounded-sm mt-1.5"
              placeholder="Heavy Duty Bracket"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Starting Quantity</Label>
            <Input
              type="number"
              min={0}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              data-testid="new-item-qty-input"
              className="rounded-sm mt-1.5"
            />
            <p className="mt-1 text-xs text-slate-500">
              Use 0 if you haven't received any yet — you can add stock later with "Receive Inventory".
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Reorder Point {janitorial ? <span className="text-red-600">*</span> : <span className="text-slate-400 normal-case">(optional)</span>}
            </Label>
            <Input
              type="number"
              min={0}
              required={janitorial}
              value={form.reorder_point}
              onChange={(e) => setForm((f) => ({ ...f, reorder_point: e.target.value }))}
              data-testid="new-item-reorder-input"
              placeholder={janitorial ? "e.g. 5" : "Leave blank for no alerts"}
              className="rounded-sm mt-1.5"
            />
            <p className="mt-1 text-xs text-slate-500">
              {janitorial
                ? "We'll flag this item on the dashboard as soon as warehouse stock drops to this number."
                : "Set a number if you want a low-stock alert. Optional for office furniture & supply."}
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Vendor <span className="text-slate-400 normal-case">(optional)</span>
            </Label>
            <Select value={form.vendor_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, vendor_id: v === "__none__" ? "" : v }))}>
              <SelectTrigger data-testid="new-item-vendor-select" className="rounded-sm mt-1.5">
                <SelectValue placeholder="No vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No vendor</SelectItem>
                {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-500">Linking a vendor makes reorder POs one-click.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="new-item-submit-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Add Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function EditReorderDialog({ open, onOpenChange, item, onSuccess, vendors = [] }) {
  const [value, setValue] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && item) {
      setValue(item.reorder_point === null || item.reorder_point === undefined ? "" : String(item.reorder_point));
      setVendorId(item.vendor_id || "");
    }
  }, [open, item]);

  if (!item) return null;
  const janitorial = item.category === "janitorial";

  const submit = async (e) => {
    e.preventDefault();
    if (janitorial && (value === "" || Number(value) < 0)) {
      return toast.error("Janitorial items need a reorder point");
    }
    setBusy(true);
    try {
      const payload = {};
      if (value === "") payload.clear_reorder_point = true;
      else payload.reorder_point = Number(value);
      if (vendorId === "") payload.clear_vendor = true;
      else payload.vendor_id = vendorId;
      await api.patch(`/inventory/items/${item.id}`, payload);
      toast.success("Item updated");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="edit-reorder-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            Edit item · {item.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <div className="font-mono text-slate-500">{item.sku} · {janitorial ? "Janitorial" : "Office"}</div>
            <div className="mt-1">Current warehouse quantity: <span className="font-mono font-semibold text-slate-900">{item.quantity}</span></div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Reorder point {janitorial ? <span className="text-red-600">*</span> : <span className="text-slate-400 normal-case">(optional)</span>}
            </Label>
            <Input
              type="number"
              min={0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="edit-reorder-input"
              placeholder={janitorial ? "e.g. 5" : "Leave blank for no alerts"}
              className="rounded-sm mt-1.5"
              required={janitorial}
            />
            <p className="mt-1 text-xs text-slate-500">
              {janitorial
                ? "We'll surface this item on the dashboard the moment stock hits this number."
                : "Blank means we'll never flag this item — good for one-off furniture."}
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Vendor</Label>
            <Select value={vendorId || "__none__"} onValueChange={(v) => setVendorId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="edit-item-vendor-select" className="rounded-sm mt-1.5">
                <SelectValue placeholder="No vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No vendor</SelectItem>
                {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-reorder-submit" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ReceiveDialog({ open, onOpenChange, items, onSuccess, initialItemId = "" }) {
  const [itemId, setItemId] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setItemId(initialItemId || ""); setQty(1); setNote(""); } }, [open, initialItemId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!itemId) return toast.error("Select an item");
    setBusy(true);
    try {
      await api.post("/inventory/receive", { item_id: itemId, quantity: Number(qty), note });
      toast.success("Inventory received");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Receive failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md" data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Receive Inventory</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Item</Label>
            <div className="mt-1.5">
              <ItemCombobox
                items={items}
                value={itemId}
                onChange={setItemId}
                testId="receive-item"
                placeholder="Type SKU or name…"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quantity</Label>
            <Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} data-testid="receive-qty-input" className="rounded-sm mt-1.5" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="receive-note-input" className="rounded-sm mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="receive-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Receive
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [expected, setExpected] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newExpectedOpen, setNewExpectedOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [tab, setTab] = useState("stock");
  const [category, setCategory] = useState("office"); // office | janitorial
  const [editTarget, setEditTarget] = useState(null);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [receivePrefill, setReceivePrefill] = useState("");
  const { canDelete } = useAuth();

  const load = async () => {
    const [{ data: itms }, { data: tx }, { data: exp }, { data: vnd }] = await Promise.all([
      api.get("/inventory/items"),
      api.get("/inventory/transactions"),
      api.get("/expected-deliveries"),
      api.get("/vendors"),
    ]);
    setItems(itms);
    setTransactions(tx);
    setExpected(exp);
    setVendors(vnd);
  };

  useEffect(() => { load(); }, []);

  // Live sync — refetch inventory + deliveries when another window mutates
  useEffect(() => {
    return subscribe((msg) => {
      if (msg?.type === "delivery-updated" || msg?.type === "inventory-updated") load();
    });
  }, []);

  const filtered = items.filter((i) => {
    if (i.category && i.category !== category) return false;
    if (!i.category && category !== "office") return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return i.name.toLowerCase().includes(q) || i.sku.toLowerCase().includes(q);
  });
  const officeCount = items.filter((i) => (i.category || "office") === "office").length;
  const janitorialCount = items.filter((i) => i.category === "janitorial").length;

  const doDeleteItem = async () => {
    if (!deleteTarget) return;
    try {
      const res = await api.delete(`/inventory/items/${deleteTarget.id}`);
      const parts = [];
      if (res.data.warehouse_qty_zeroed) parts.push(`warehouse ${res.data.warehouse_qty_zeroed} zeroed`);
      if (res.data.onsite_zeroed) parts.push(`on-site ${res.data.onsite_zeroed} zeroed`);
      const suffix = parts.length ? ` — ${parts.join(", ")}` : "";
      toast.success(`Removed "${deleteTarget.name}"${suffix}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
      throw e;
    }
  };

  const doCancelExpected = async (delivery) => {
    try {
      await api.delete(`/expected-deliveries/${delivery.id}`);
      toast.success("Expected delivery cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const downloadExport = async (path, filenameFallback) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      a.download = match ? match[1] : filenameFallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handleBarcodeDetected = (code) => {
    // Case-insensitive SKU match
    const found = items.find((i) => (i.sku || "").toUpperCase() === code);
    if (!found) {
      toast.error(`No item found for "${code}"`);
      return;
    }
    // Auto-jump to that item's category so it's visible in the list
    if (found.category && found.category !== category) setCategory(found.category);
    toast.success(`Matched ${found.name} · opening receive`);
    setReceivePrefill(found.id);
    setReceiveOpen(true);
  };

  const openExpected = expected.filter((d) => d.status !== "complete");
  const doneExpected = expected.filter((d) => d.status === "complete");
  const unreviewedExtrasCount = expected.reduce(
    (n, d) => n + (d.lines || []).filter((l) => l.unexpected && !l.reviewed).length,
    0
  );

  return (
    <div>
      <PageHeader
        eyebrow="Stock"
        title="Warehouse Inventory"
        description="Physical stock, plus expected deliveries you can pre-load before a truck rolls out."
        testId="inventory-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="inventory-print-btn" />
            {tab === "stock" ? (
              <>
                <Button
                  onClick={() => setBarcodeOpen(true)}
                  data-testid="scan-barcode-btn"
                  variant="outline"
                  className="rounded-sm border-slate-300"
                >
                  <ScanBarcode size={16} className="mr-1.5" /> Scan Barcode
                </Button>
                <Button
                  onClick={() => downloadExport(`/inventory/items/export.csv?category=${category}`, `inventory-${category}.csv`)}
                  data-testid="export-stock-btn"
                  variant="outline"
                  className="rounded-sm border-slate-300"
                >
                  <Download size={16} className="mr-1.5" /> Export Stock
                </Button>
                <Button
                  onClick={() => setScanOpen(true)}
                  data-testid="scan-list-btn"
                  variant="outline"
                  className="rounded-sm border-slate-300"
                >
                  <ScanLine size={16} className="mr-1.5" /> Scan List
                </Button>
                <Button
                  onClick={() => setNewItemOpen(true)}
                  data-testid="new-item-btn"
                  variant="outline"
                  className="rounded-sm border-slate-300"
                >
                  <Plus size={16} className="mr-1.5" /> New Item
                </Button>
                <Button onClick={() => setReceiveOpen(true)} data-testid="open-receive-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
                  <PackagePlus size={16} className="mr-1.5" /> Receive Inventory
                </Button>
              </>
            ) : tab === "expected" ? (
              <Button onClick={() => setNewExpectedOpen(true)} data-testid="new-expected-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
                <Truck size={16} className="mr-1.5" /> New Expected Delivery
              </Button>
            ) : tab === "log" ? (
              <Button
                onClick={() => downloadExport("/inventory/transactions/export.csv", "transactions.csv")}
                data-testid="export-transactions-btn"
                variant="outline"
                className="rounded-sm border-slate-300"
              >
                <Download size={16} className="mr-1.5" /> Export Transactions
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="px-8 pt-6 print-container">
        <PrintHeader title="Warehouse Inventory" subtitle={`${items.length} items in catalog`} />
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-slate-100 no-print">
            <TabsTrigger value="stock" data-testid="inv-tab-stock" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Stock
            </TabsTrigger>
            <TabsTrigger value="expected" data-testid="inv-tab-expected" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Expected Deliveries
              {openExpected.length > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold font-mono rounded-sm bg-blue-800 text-white">
                  {openExpected.length}
                </span>
              )}
              {unreviewedExtrasCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold font-mono rounded-sm bg-red-600 text-white animate-pulse"
                  data-testid="unreviewed-extras-badge"
                  title={`${unreviewedExtrasCount} unexpected items awaiting review`}
                >
                  <AlertTriangle size={10} /> {unreviewedExtrasCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="log" data-testid="inv-tab-log" className="rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Transaction Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stock" className="mt-6 space-y-6">
            <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden no-print" data-testid="category-switch">
              <button
                type="button"
                onClick={() => setCategory("office")}
                data-testid="cat-office-btn"
                className={`px-4 py-2 text-sm font-semibold transition-colors ${category === "office" ? "bg-blue-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                Office Furniture & Supply
                <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${category === "office" ? "bg-blue-900 text-blue-100" : "bg-slate-100 text-slate-600"}`}>{officeCount}</span>
              </button>
              <button
                type="button"
                onClick={() => setCategory("janitorial")}
                data-testid="cat-janitorial-btn"
                className={`px-4 py-2 text-sm font-semibold border-l border-slate-300 transition-colors ${category === "janitorial" ? "bg-blue-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                Janitorial
                <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${category === "janitorial" ? "bg-blue-900 text-blue-100" : "bg-slate-100 text-slate-600"}`}>{janitorialCount}</span>
              </button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="relative w-full max-w-sm">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search items or SKUs…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="inventory-search-input"
                  className="pl-9 rounded-sm border-slate-300"
                />
              </div>
            </div>

            <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
              <table className="w-full text-sm min-w-[820px]" data-testid="inventory-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <th className="p-4">Item</th>
                    <th className="p-4">SKU</th>
                    <th className="p-4">Unit</th>
                    <th className="p-4 text-right">Reorder Pt</th>
                    <th className="p-4 text-right">Warehouse Qty</th>
                    <th className="p-4 w-16 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-12 text-center text-slate-500 font-mono text-sm">
                        No items in catalog.
                      </td>
                    </tr>
                  ) : filtered.map((i) => {
                    const hasThreshold = i.reorder_point !== null && i.reorder_point !== undefined;
                    const isLow = hasThreshold && (i.quantity ?? 0) <= i.reorder_point;
                    return (
                    <tr key={i.id} className={`border-b border-slate-200 last:border-b-0 transition-colors ${isLow ? "bg-red-50/60 hover:bg-red-50" : "hover:bg-slate-50"}`} data-testid={`inventory-row-${i.id}`}>
                      <td className="p-4 font-semibold text-slate-900">
                        <div className="flex items-center gap-2">
                          <span>{i.name}</span>
                          {isLow && (
                            <span
                              data-testid={`low-stock-badge-${i.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-red-600 text-white"
                            >
                              Low
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs text-slate-500">{i.sku}</td>
                      <td className="p-4 text-slate-700">{i.unit}</td>
                      <td className="p-4 text-right font-mono text-xs text-slate-500">
                        {hasThreshold ? i.reorder_point : <span className="text-slate-300">—</span>}
                      </td>
                      <td className={`p-4 text-right font-mono font-semibold ${isLow ? "text-red-700" : "text-slate-900"}`}>{i.quantity}</td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Item actions"
                              data-testid={`item-actions-${i.id}`}
                              className="h-8 w-8 rounded-sm hover:bg-slate-100"
                            >
                              <MoreHorizontal size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56 rounded-sm">
                            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">
                              Actions
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setEditTarget(i)}
                              data-testid={`item-edit-${i.id}`}
                            >
                              <Pencil size={14} className="mr-2" /> Edit item
                            </DropdownMenuItem>
                            {canDelete ? (
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(i)}
                                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                data-testid={`item-delete-${i.id}`}
                              >
                                <Trash2 size={14} className="mr-2" /> Remove Item
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem disabled data-testid={`item-delete-disabled-${i.id}`}>
                                <Lock size={14} className="mr-2" /> Remove (Admin only)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="expected" className="mt-6 space-y-4" data-testid="expected-tab">
            {expected.length === 0 ? (
              <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center">
                <Truck size={40} className="mx-auto text-slate-400" />
                <div className="mt-3 font-display text-xl font-bold text-slate-900">No expected deliveries yet</div>
                <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
                  Pre-load a truckload here. Installers on-site will just type the SKU and how many arrived — lines auto-check off.
                </p>
                <Button
                  onClick={() => setNewExpectedOpen(true)}
                  className="mt-4 bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
                  data-testid="new-expected-empty-btn"
                >
                  <Plus size={16} className="mr-1.5" /> Schedule First Delivery
                </Button>
              </div>
            ) : (
              <>
                {openExpected.length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Open</div>
                    <div className="space-y-3">
                      {openExpected.map((d) => (
                        <ExpectedCard key={d.id} d={d} onCancel={() => doCancelExpected(d)} canDelete={canDelete} onReviewed={load} />
                      ))}
                    </div>
                  </div>
                )}
                {doneExpected.length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 mt-6">Completed</div>
                    <div className="space-y-3">
                      {doneExpected.map((d) => (
                        <ExpectedCard key={d.id} d={d} canDelete={canDelete} onReviewed={load} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="log" className="mt-6">
            <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
              {transactions.length ? (
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                      <th className="p-4">Date</th>
                      <th className="p-4">Type</th>
                      <th className="p-4">Item</th>
                      <th className="p-4">Project</th>
                      <th className="p-4 text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice(0, 100).map((t) => {
                      const badge =
                        t.type === "transfer" ? "bg-blue-100 text-blue-800" :
                        t.type === "return" ? "bg-emerald-100 text-emerald-800" :
                        t.type === "site_delivery" ? "bg-indigo-100 text-indigo-800" :
                        t.type === "adjustment" ? "bg-amber-100 text-amber-900" :
                        "bg-slate-100 text-slate-700";
                      const label = t.type === "site_delivery" ? "site delivery" : t.type;
                      return (
                        <tr key={t.id} className="border-b border-slate-200 last:border-b-0">
                          <td className="p-4 font-mono text-xs text-slate-500">{formatDate(t.created_at)}</td>
                          <td className="p-4">
                            <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-sm ${badge}`}>{label}</span>
                          </td>
                          <td className="p-4 text-slate-900">{t.item_name}</td>
                          <td className="p-4 text-slate-700">{t.project_name || "—"}</td>
                          <td className="p-4 text-right font-mono font-semibold">{t.quantity}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">No transactions yet.</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ReceiveDialog open={receiveOpen} onOpenChange={(o) => { setReceiveOpen(o); if (!o) setReceivePrefill(""); }} items={items} onSuccess={load} initialItemId={receivePrefill} />
      <NewItemDialog open={newItemOpen} onOpenChange={setNewItemOpen} defaultCategory={category} onSuccess={load} vendors={vendors} />
      <NewExpectedDeliveryDialog open={newExpectedOpen} onOpenChange={setNewExpectedOpen} onSuccess={load} />
      <ScanInventoryDialog open={scanOpen} onOpenChange={setScanOpen} defaultCategory={category} onSuccess={load} />
      <EditReorderDialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)} item={editTarget} onSuccess={load} vendors={vendors} />
      <BarcodeScannerDialog open={barcodeOpen} onOpenChange={setBarcodeOpen} onDetected={handleBarcodeDetected} title="Scan box barcode" />
      <DeleteInventoryItemDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        item={deleteTarget}
        onDelete={doDeleteItem}
      />
    </div>
  );
}

function ExpectedCard({ d, onCancel, canDelete, onReviewed }) {
  const statusCls =
    d.status === "complete" ? "bg-emerald-100 text-emerald-800" :
    d.status === "partial" ? "bg-blue-100 text-blue-800" :
    "bg-slate-100 text-slate-700";
  const unreviewedExtras = (d.lines || []).filter((l) => l.unexpected && !l.reviewed);
  const markReviewed = async (line) => {
    try {
      await api.post(`/expected-deliveries/${d.id}/lines/${line.id}/review`);
      toast.success(`Marked ${line.sku} as reviewed`);
      onReviewed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark as reviewed");
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto" data-testid={`expected-card-${d.id}`}>
      <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${statusCls}`}>
              {d.status}
            </span>
            <span className="font-mono text-sm font-semibold text-slate-900">{d.reference || "no reference"}</span>
            {unreviewedExtras.length > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-red-100 text-red-800 animate-pulse"
                data-testid={`extras-badge-${d.id}`}
              >
                <AlertTriangle size={10} /> {unreviewedExtras.length} unexpected · review
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-700 font-semibold">{d.project_name}</div>
          {d.note && <div className="text-xs text-slate-500 mt-0.5">{d.note}</div>}
        </div>
        <div className="grid grid-cols-4 gap-4 text-xs">
          <Metric label="Expected" value={d.totals.expected} />
          <Metric label="Received" value={d.totals.received} />
          <Metric label="Remaining" value={d.totals.remaining} tone={d.totals.remaining ? "amber" : "default"} />
          <Metric label="Over" value={d.totals.over} tone={d.totals.over ? "amber" : "default"} />
        </div>
        {canDelete && onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid={`cancel-expected-${d.id}`}
            className="text-slate-500 hover:text-red-600 rounded-sm"
          >
            <Trash size={14} className="mr-1.5" /> Cancel
          </Button>
        )}
      </div>
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <th className="p-2.5 w-8"></th>
            <th className="p-2.5">SKU</th>
            <th className="p-2.5">Item</th>
            <th className="p-2.5 text-right">Received / Expected</th>
            <th className="p-2.5 text-right">Status</th>
            <th className="p-2.5 text-right w-32"></th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l) => {
            const done = l.status === "received" || l.status === "over" || l.status === "extra";
            const isExtra = l.unexpected;
            const needsReview = isExtra && !l.reviewed;
            return (
              <tr
                key={l.id}
                className={`border-b border-slate-200 last:border-b-0 ${needsReview ? "bg-red-50/50" : ""}`}
                data-testid={`expected-line-row-${l.id}`}
              >
                <td className="p-2.5">
                  {done ? (
                    <CheckCircle2 size={16} className={isExtra ? "text-purple-600" : "text-emerald-600"} />
                  ) : (
                    <div className="h-4 w-4 border-2 border-slate-300 rounded-sm" />
                  )}
                </td>
                <td className="p-2.5 font-mono text-xs text-slate-700">{l.sku}</td>
                <td className="p-2.5 text-slate-900">
                  <div className="flex items-center gap-2">
                    <span>{l.item_name}</span>
                    {isExtra && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800">
                        <AlertTriangle size={9} /> Unexpected
                      </span>
                    )}
                    {isExtra && l.reviewed && (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">
                        Reviewed
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-2.5 text-right font-mono">
                  {isExtra ? `${l.received_quantity} · extra` : `${l.received_quantity} / ${l.expected_quantity}`}
                </td>
                <td className="p-2.5 text-right">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${
                    l.status === "received" ? "bg-emerald-100 text-emerald-800" :
                    l.status === "over" ? "bg-amber-100 text-amber-900" :
                    l.status === "partial" ? "bg-blue-100 text-blue-800" :
                    l.status === "extra" ? "bg-purple-100 text-purple-800" :
                    "bg-slate-100 text-slate-700"
                  }`}>{l.status}</span>
                </td>
                <td className="p-2.5 text-right">
                  {needsReview && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markReviewed(l)}
                      data-testid={`mark-reviewed-${l.id}`}
                      className="h-7 rounded-sm text-xs font-semibold border-red-200 text-red-700 hover:bg-red-50"
                    >
                      <CheckCircle2 size={12} className="mr-1" /> Mark reviewed
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Metric({ label, value, tone = "default" }) {
  const cls = tone === "amber" ? "text-amber-800" : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`font-mono font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
